import KenshinTable from 'components/KenshinTable';
import { TitleColumn, CreatedAtColumn, OthersColumn } from '../commonColumns';
import { useMemo, useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import BatchEditModal from 'views/Kenshin/ReceivedKenshin/components/BatchEditModal';
import { useSnackbar } from 'notistack';
import { getFirstDayPreviousMonth } from 'utils/dateTime';
import { USER_DATA } from 'shared/constants/users';
import localData from 'utils/localData';
import { USER_COMMENT } from 'shared/constants/comment';
import { KENSHIN_API } from 'shared/constants/apis';
import PropTypes from 'prop-types';
import useApi from 'shared/hooks/api';
import { PageLoader, PageError } from 'components';
import { updateArrayItemById } from 'utils/javascript';
import intl from 'react-intl-universal';
import { IssueStatusKenshin } from 'shared/constants/kenshin/issues';
import { ReceivedProgressColumn, FromColumn } from './columns';
import logger from 'utils/logger';
import { TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
const propTypes = {
  type: PropTypes.string,
};

const log = logger.createLogger('ReceivedKenshin');

const ReceivedKenshin = ({ type }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);
  const firstDayPreviousMonth = getFirstDayPreviousMonth(currentDate);
  const [timeRangeStart, setTimeRangeStart] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.KENSHIN_START_DATE);
    return saved ? new Date(saved) : firstDayPreviousMonth;
  });
  const [timeRangeEnd, setTimeRangeEnd] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.KENSHIN_END_DATE);
    return saved ? new Date(saved) : currentDate;
  });
  const [batchEditModalIsOpen, setBatchEditModalIsOpen] = useState(false);
  const [selectedIssueIndexes, setSelectedIssueIndexes] = useState([]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.KENSHIN_START_DATE,
      timeRangeStart.toISOString()
    );
  }, [timeRangeStart]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.KENSHIN_END_DATE,
      timeRangeEnd.toISOString()
    );
  }, [timeRangeEnd]);

  const handleOpenIssueDetails = (issueId) => {
    navigate(`${location.pathname}/issues/${issueId}`);
  };

  const columns = useMemo(
    () => [
      TitleColumn(),
      ReceivedProgressColumn(),
      CreatedAtColumn(),
      FromColumn(),
      OthersColumn({ handleOpenIssueDetails }),
    ],
    []
  );

  const currentUsername = localData.get(USER_DATA.USERNAME);
  const userId = localData.get(USER_COMMENT.USER_ID);

  const [{ data, error, setLocalData }, fetchProject] = useApi.useGet(
    KENSHIN_API.ISSUES_RECEIVED,
    {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        type: type,
        timeRangeStart: timeRangeStart,
        timeRangeEnd: timeRangeEnd,
        username: currentUsername,
        userId: userId,
      },
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
    }
  );

  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { receivedIssues, doctors } = data;

  const updateLocalProjectIssues = (issueId, updatedFields) => {
    setLocalData((currentData) => ({
      receivedIssues: updateArrayItemById(
        currentData.receivedIssues,
        issueId,
        updatedFields
      ),
      doctors: doctors,
    }));
    // the function passed into the brackets of setLocalData(...) is the `getUpdatedData` in setLocalData in /shared/hooks/api/query.js;
    // Therefore currentData is `data` in the line `const updatedData = getUpdatedData(data);`;
  };

  const closeBatchEditModal = () => {
    setBatchEditModalIsOpen(false);
  };

  const handleRefreshClick = async () => {
    // Updating search times as the table will contain the same issues if time is not updated
    if (timeRangeEnd.toDateString() === new Date().toDateString()) {
      setTimeRangeEnd(new Date());
    }
    try {
      await fetchProject();
      enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      log.error('Error in handleRefreshClick', { error });
      enqueueSnackbar(intl.get('shared_table_data_reload_error'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  receivedIssues.sort((issue1, issue2) => issue2.id - issue1.id);
  const tableAllData = receivedIssues.map((issue, index) => {
    return {
      index: index,
      title: issue.title,
      progress: IssueStatusKenshin[issue.progress],
      createdAt: issue.createdAt,
      srcOrgName: issue.srcOrgName,
      id: issue.id,
    };
  });

  const handleBatchEditClick = (selectedRows) => {
    const selectedIndexes = selectedRows.map((row) => row.index);
    setSelectedIssueIndexes(selectedIndexes);
    setBatchEditModalIsOpen(true);
  };

  return (
    <div>
      <KenshinTable
        columns={columns}
        tableTitle={intl.get('kenshin_received_history_table_title')}
        timeRangeStart={timeRangeStart}
        timeRangeEnd={timeRangeEnd}
        updateTimeRangeStart={setTimeRangeStart}
        updateTimeRangeEnd={setTimeRangeEnd}
        data={tableAllData}
        refreshData={handleRefreshClick}
        handleBatchEditClick={handleBatchEditClick}
      />
      <Outlet
        context={{
          doctors,
          updateLocalProjectIssues,
        }}
      />
      {batchEditModalIsOpen && (
        <BatchEditModal
          isOpened={batchEditModalIsOpen}
          closeModal={closeBatchEditModal}
          issues={receivedIssues}
          selectedIssueIndexes={selectedIssueIndexes}
          doctors={doctors}
          updateLocalProjectIssues={updateLocalProjectIssues}
          handleOpenIssueDetails={handleOpenIssueDetails}
        />
      )}
    </div>
  );
};

ReceivedKenshin.propTypes = propTypes;

export default ReceivedKenshin;
